import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import { observable } from "mobx";
import { observer } from "mobx-react";
import { withTranslation } from "react-i18next";
import { Ref, Button, Icon } from "semantic-ui-react";
import StickyMenu from "~/components/StickyMenu";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import ContentLoader from "~/components/element/ContentLoader";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import UiMeet3dLayoutWrapper from "~/modules/UiMeet3d/components/UiMeet3dLayoutWrapper";
import UiMeet3dNav from "~/modules/UiMeet3d/constants/UiMeet3dNav";
import FormHelper from "~/utils/FormHelper";
import FormStore from "~/stores/FormStore";
import UiMeet3dPhotoStore from "~/stores/UiMeet3d/UiMeet3dPhotoStore";
import UiMeet3dGeneralForm from "~/modules/UiMeet3d/components/UiMeet3dGeneralForm";
import UiMeet3dPhotoType from "~/constants/UiMeet3d/UiMeet3dPhotoType";

@observer
class UiMeet3dGeneralPage extends Component {
  ref = null;

  @observable formStore = null;

  @observable uiMeet3dPhotoStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    this.ref = createRef();
    document.title = `${t("uiMeet3d")} - ${t("general")}`;

    this.formStore = new FormStore();
    this.uiMeet3dPhotoStore = new UiMeet3dPhotoStore();
  }

  componentDidMount() {
    const { showError, actualUserStore } = this.props;

    this.uiMeet3dPhotoStore.loadIndex().catch(error => showError(error.message));

    this.bindForm(actualUserStore);
  }

  onSubmit() {
    const { showError, toastMessageStore, t, actualUserStore } = this.props;
    const { actualUser } = actualUserStore;
    const { model } = this.formStore;
    const {
      uiMeet3dLevel,
      mainUserAvatarPhotoData,
      deleteMainUserAvatarPhoto,
      mainUserBackBadgePhotoData,
      deleteMainUserBackBadgePhoto,
    } = model;
    const { uiMeet3dPhotos } = this.uiMeet3dPhotoStore;

    const formActualUser = {
      uiMeet3dLevel: FormHelper.convertToValue(uiMeet3dLevel),
    };

    if (actualUserStore instanceof ProviderStore) {
      actualUserStore.providerId = actualUser.providerId;
    } else {
      actualUserStore.customerId = actualUser.customerId;
    }

    const actualUserSavePromise = actualUserStore
      .save(formActualUser)
      .then(() => {
        toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
      })
      .catch(error => {
        if (error.status === "error") {
          showError(error.message);
        }
      });

    let mainUserAvatarPhotoCreatePromise = Promise.resolve();
    let mainUserAvatarPhotoDeletePromise = Promise.resolve();

    const mainUserAvatarPhoto = this.uiMeet3dPhotoStore.getUiMeet3dPhotoByType(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_AVATAR,
    );

    if (deleteMainUserAvatarPhoto) {
      mainUserAvatarPhotoDeletePromise = this.uiMeet3dPhotoStore
        .delete(mainUserAvatarPhoto.uiMeet3dPhotoId)
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:photoDeleted"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    } else if (mainUserAvatarPhotoData !== "") {
      mainUserAvatarPhotoCreatePromise = this.uiMeet3dPhotoStore
        .create({
          type: UiMeet3dPhotoType.MAIN_USER_AVATAR,
          photoData: mainUserAvatarPhotoData,
        })
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:newPhotoSaved"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    }

    let mainUserBackBadgePhotoCreatePromise = Promise.resolve();
    let mainUserBackBadgePhotoDeletePromise = Promise.resolve();

    const mainUserBackBadgePhoto = this.uiMeet3dPhotoStore.getUiMeet3dPhotoByType(
      uiMeet3dPhotos,
      UiMeet3dPhotoType.MAIN_USER_BACK_BADGE,
    );

    if (deleteMainUserBackBadgePhoto) {
      mainUserBackBadgePhotoDeletePromise = this.uiMeet3dPhotoStore
        .delete(mainUserBackBadgePhoto.uiMeet3dPhotoId)
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:photoDeleted"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    } else if (mainUserBackBadgePhotoData !== "") {
      mainUserBackBadgePhotoCreatePromise = this.uiMeet3dPhotoStore
        .create({
          type: UiMeet3dPhotoType.MAIN_USER_BACK_BADGE,
          photoData: mainUserBackBadgePhotoData,
        })
        .then(() => {
          toastMessageStore.addToastMessage(t("clientPanelMisc:newPhotoSaved"), "positive", t("success"));
        })
        .catch(error => {
          if (error.status === "error") {
            showError(error.message);
          }
        });
    }

    actualUserSavePromise
      .then(mainUserAvatarPhotoCreatePromise)
      .then(mainUserAvatarPhotoDeletePromise)
      .then(mainUserBackBadgePhotoCreatePromise)
      .then(mainUserBackBadgePhotoDeletePromise)
      .then(() => {
        this.uiMeet3dPhotoStore.loadIndex().catch(error => showError(error.message));

        this.bindForm(actualUserStore);
      });
  }

  bindForm(actualUserStore) {
    const { actualUser } = actualUserStore;
    let uiMeet3dLevelAsString;

    if (actualUserStore instanceof ProviderStore) {
      uiMeet3dLevelAsString = FormHelper.convertToString(actualUser.uiMeet3dLevelCoalesced);
    } else {
      uiMeet3dLevelAsString = FormHelper.convertToString(actualUser.uiMeet3dLevel);
    }

    const initialModel = {
      mainUserAvatarPhotoData: "",
      deleteMainUserAvatarPhoto: false,
      mainUserBackBadgePhotoData: "",
      deleteMainUserBackBadgePhoto: false,
      uiMeet3dLevel: uiMeet3dLevelAsString,
    };

    this.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore, actualUserStore } = this.props;
    const { isLoading: actualUserLoading, errorMessages, isSaving: actualUserSaving, actualUser } = actualUserStore;
    const { isLoading: uiMeet3dPhotoLoading, isSaving: uiMeet3dPhotoSaving } = this.uiMeet3dPhotoStore;
    const { isDirty } = this.formStore;

    if (actualUserLoading || uiMeet3dPhotoLoading) {
      return <ContentLoader />;
    }

    const isSaving = actualUserSaving || uiMeet3dPhotoSaving;

    return (
      <>
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <div className="side-page-outer-container" style={{ marginTop: "0" }}>
          <Ref innerRef={this.ref}>
            <div>
              <StickyMenu
                contextRef={this.ref}
                headerText={t("general")}
                button={
                  <div className="side-page-save-btn-wrap">
                    <Button primary icon disabled={!isDirty} onClick={() => this.onSubmit()}>
                      <Icon name="save" />
                      {t("saveChanges")}
                    </Button>
                  </div>
                }
              />
              <UiMeet3dGeneralForm
                actualUser={actualUser}
                uiMeet3dPhotoStore={this.uiMeet3dPhotoStore}
                formStore={this.formStore}
                validationErrors={typeof errorMessages === "object" ? errorMessages : null}
              />
            </div>
          </Ref>
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

UiMeet3dGeneralPage.propTypes = {
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  showError: PropTypes.func.isRequired,
};

export default withTranslation()(ErrorWrapper(UiMeet3dLayoutWrapper(UiMeet3dGeneralPage, UiMeet3dNav.GENERAL)));
